// Helpers

import { merge } from "lodash";
import { local_schema, ILocalState$, IRemoteState$, ICreateStateProps, IObservers, IActions } from "./types";
import { api, IAPI, IContentExtract, IMeasure } from "@/core/shared/api";
import { transforms } from "@/core/shared/transforms";
import { z } from "zod";
import { legend } from "@/core/utils/legend-state/core";
import { rc } from "../../rc";
import { auth } from "@/core/state/auth";
import { metadata } from "@/core/state/metadata";
import { report_builder } from "@/features/report-builder/state";
import { ITransformedContentExtract } from "../../../extracts/instance/state/types";
import { IPeersetCompany } from "@/features/report-builder/state/types";

// Config
const config: z.infer<typeof local_schema>["config"] = {
  company: {
    measure_filter_to_measure_name_map: {
      NSS: {
        default: "NSS_PER_COMPANY_PER_TOPIC",
        withGeoContinent: "NSS_PER_COMPANY_PER_CONTINENT_PER_TOPIC",
        withGeoCountry: "NSS_PER_COMPANY_PER_COUNTRY_PER_TOPIC",
      },
      PREVALENCE: {
        default: "PREVALENCE_PER_COMPANY_PER_TOPIC",
        withGeoContinent: "PREVALENCE_PER_COMPANY_PER_CONTINENT_PER_TOPIC",
        withGeoCountry: "PREVALENCE_PER_COMPANY_PER_COUNTRY_PER_TOPIC",
      },
      RATING: {
        default: "RATING_PER_COMPANY_PER_TOPIC",
        withGeoContinent: "RATING_PER_COMPANY_PER_CONTINENT_PER_TOPIC",
        withGeoCountry: "RATING_PER_COMPANY_PER_COUNTRY_PER_TOPIC",
      },
      REVIEW_COUNT: {
        default: "COUNT_PER_COMPANY_PER_TOPIC",
        withGeoContinent: "COUNT_PER_COMPANY_PER_CONTINENT_PER_TOPIC",
        withGeoCountry: "COUNT_PER_COMPANY_PER_COUNTRY_PER_TOPIC",
      },
    },
    filters: {
      hide: false,
    },
  },
  peer_set_avg: {
    PEER_SET_AVG_MEAN: {
      id: "PEER_SET_AVG_MEAN",
      label: "Mean",
    },
    PEER_SET_AVG_MEDIAN: {
      id: "PEER_SET_AVG_MEDIAN",
      label: "Median",
    },
  },
  measure: {
    hide: false,
    default: "NSS",
    measures: [
      // {
      //   id: "NSS",
      //   label: "Employee Net Sentiment Score (NSS)",
      //   unit: "%",
      //   acronym: "NSS",
      //   showSign: true,
      //   domain: [-100, 100],
      //   filter: {
      //     checked: true,
      //     disabled: false,
      //   },
      // },
      {
        id: "PREVALENCE",
        label: "Topic Frequency ",
        unit: "%",
        acronym: "PREV",
        domain: undefined,
        filter: {
          checked: true,
          disabled: false,
        },
      },
      // {
      //   id: "RATING",
      //   label: "Employee Rating",
      //   domain: [0, 5],
      //   filter: {
      //     checked: true,
      //     disabled: false,
      //   },
      // },
      {
        id: "REVIEW_COUNT",
        label: "Employee Review Count",
        domain: undefined,
        compactYAxisValues: true,
        filter: {
          checked: true,
          disabled: false,
        },
      },
    ],
  },
  benchmark: {
    hide: false,
    benchmarks: [
      {
        id: "PEER_SET_AVG",
        label: "Peer set average",
        measure_filter_to_measure_name_map: {
          NSS: {
            default: "NSS_PER_TOPIC",
            withGeoContinent: "NSS_PER_CONTINENT_PER_TOPIC",
            withGeoCountry: "NSS_PER_COUNTRY_PER_TOPIC",
          },
          PREVALENCE: {
            default: "PREVALENCE_PER_TOPIC",
            withGeoContinent: "PREVALENCE_PER_CONTINENT_PER_TOPIC",
            withGeoCountry: "PREVALENCE_PER_COUNTRY_PER_TOPIC",
          },
          RATING: {
            default: "RATING_PER_TOPIC",
            withGeoContinent: "RATING_PER_CONTINENT_PER_TOPIC",
            withGeoCountry: "RATING_PER_COUNTRY_PER_TOPIC",
          },
          REVIEW_COUNT: {
            default: "COUNT_PER_TOPIC",
            withGeoContinent: "COUNT_PER_CONTINENT_PER_TOPIC",
            withGeoCountry: "COUNT_PER_COUNTRY_PER_TOPIC",
          },
        },
        filter: {
          checked: true,
          disabled: false,
        },
      },
    ],
  },
  visualisation: [
    // {
    //   id: "radar",
    //   label: "Radar",
    // },
    // {
    //   id: "bar",
    //   label: "Bar",
    // },
    {
      id: "table",
      label: "Table",
    },
  ],
};

export function createState(
  instance_id: string,
  props: ICreateStateProps,
): { local: ILocalState$; remote: IRemoteState$ } {
  const local = helpers.validateAndMergeState(
    {
      label: "Employee Voice: Company Personality",
      ui_merged: false,
      config,
      views: {
        currentView: "analysis",
        analysis: {
          chart_config: {
            entities: {},
            polar_axis: {
              data_key: "topic",
              label: "Topic",
              domain: config.measure.measures.find((m) => m.id === "REVIEW_COUNT")?.domain,
            },
          },
          selectors: {
            measure: {
              selectedMeasureId: "REVIEW_COUNT",
            },
            visualisation: {
              selected: "table",
            },
            peer_set_avg_type: {
              selected: config.peer_set_avg.PEER_SET_AVG_MEAN.id,
              applied: config.peer_set_avg.PEER_SET_AVG_MEAN.id,
            },
            peer_set_avg_mode: {
              selected: true,
              applied: true,
            },
            geo: {
              selectedLocationId: undefined,
              selectedLocationType: undefined,
            },
          },
          filters: {
            company: legend.features.filter.createObservableState(),
            peer_set_avg_contributors: legend.features.filter.createObservableState(),
            topic: legend.features.filter.createObservableState(),
            timeframe: legend.features.filter.createObservableState(),
            benchmark: legend.features.filter.createObservableState(),
            applied_filters: {
              company_id: [],
              topic_name: [],
              timeframe: [],
              benchmarks: [],
              peer_set_avg_contributors: [],
            },
          },
        },
      },
    },
    props.local,
  );

  const remote: IRemoteState$ = {
    measures: {
      pending: false,
      error: false,
      data: legend.sync.synced({
        initial: [],
        get: async () => {
          const instance = rc.registry.actions.getInstance(instance_id);
          try {
            instance.remote.measures.pending.set(true);
            const selected_measure_id = instance.local.views.analysis.selectors.measure.selectedMeasureId.get();

            const selected_location_id = instance.local.views.analysis.selectors.geo.selectedLocationId.get();
            const selected_location_type = instance.local.views.analysis.selectors.geo.selectedLocationType.get();

            // convert benchmarks to measure names
            const benchmark_measure_names: string[] = [];
            instance.local.views.analysis.filters.applied_filters.benchmarks.get()?.forEach((benchmark_id) => {
              const benchmark = instance.local.config.benchmark.benchmarks.get().find((b) => b.id === benchmark_id);
              if (!benchmark) return;
              const benchmark_measure_name = helpers.mapMeasureNameToParams(
                benchmark.measure_filter_to_measure_name_map,
                selected_measure_id,
                selected_location_id,
                selected_location_type,
              );
              benchmark_measure_names.push(benchmark_measure_name);
            });
            const peerset = report_builder.remote.peerset.data.get();

            const benchmark_config = instance.local.config.benchmark.benchmarks.get();
            const company_config = instance.local.config.company.get();

            const applied_company_id = instance.local.views.analysis.filters.applied_filters.company_id.get();
            const applied_topic_name = instance.local.views.analysis.filters.applied_filters.topic_name.get();
            const applied_timeframe = instance.local.views.analysis.filters.applied_filters.timeframe.get();
            const applied_peer_set_avg_contributors =
              instance.local.views.analysis.filters.applied_filters.peer_set_avg_contributors.get();

            const applied_peer_set_avg_type = instance.local.views.analysis.selectors.peer_set_avg_type.applied.get();

            const company_measure_name = helpers.mapMeasureNameToParams(
              instance.local.config.company.measure_filter_to_measure_name_map.get(),
              selected_measure_id,
              selected_location_id,
              selected_location_type,
            );

            const params: Parameters<IAPI["deltabase_data_api"]["measures"]["get"]>["0"] = {
              company_id: applied_company_id,
              topic_name: applied_topic_name,
              year: applied_timeframe,
              measure_name: [company_measure_name],
              sort_field: "year",
              sort_direction: "ASC",
            };

            const peer_set_avg_params: Parameters<IAPI["deltabase_data_api"]["measures"]["get"]>["0"] = {
              company_id: applied_peer_set_avg_contributors,
              topic_name: applied_topic_name,
              year: applied_timeframe,
              measure_name: Array.from(new Set([...benchmark_measure_names])),
              sort_field: "year",
              sort_direction: "ASC",
              use_median: applied_peer_set_avg_type === "PEER_SET_AVG_MEDIAN",
            };

            if (selected_location_type === "continent") {
              params.continent = selected_location_id;
              peer_set_avg_params.continent = selected_location_id;
            } else if (selected_location_type === "country") {
              params.country = selected_location_id;
              peer_set_avg_params.country = selected_location_id;
            }

            // Guard against no company selected - Data API will return an error 400 otherwise
            if (params.company_id?.length === 0 || peer_set_avg_params.company_id?.length === 0) {
              return [];
            }

            const promises = [api.deltabase_data_api.measures.get(params)];
            // if (benchmark_measure_names.length > 0) {
            //   promises.push(api.deltabase_data_api.measures.get(peer_set_avg_params));
            // }

            const [measures_response, peer_set_avg_response] = await Promise.all(promises);

            const peer_set_avg_data = peer_set_avg_response?.data ?? [];
            const data = [...measures_response.data, ...peer_set_avg_data];

            // group by topic
            const chart_data = helpers.formatChartData({
              data,
              selected_measure_id,
              benchmark_config,
              company_config,
              selected_location_id,
              selected_location_type,
            });

            const table_data = helpers.formatTableData({
              data,
              selected_measure_id,
              benchmark_config,
              company_config,
              peerset,
              selected_location_id,
              selected_location_type,
            });

            legend.state.batch(() => {
              instance.remote.measures.pending.set(false);
              instance.remote.measures.error.set(false);
            });

            return { chart_data, table_data };
          } catch (error) {
            legend.state.batch(() => {
              instance.remote.measures.pending.set(false);
              instance.remote.measures.error.set(true);
            });
          }
        },
        waitFor: () => {
          const token = auth.state$.token.get();
          return !!token;
        },
      }),
    },
    evidence: {
      pending: false,
      error: false,
      data: legend.sync.synced({
        initial: {},
        get: async () => {
          const instance = rc.registry.actions.getInstance(instance_id);
          try {
            instance.remote.evidence.pending.set(true);
            const topic_metadata = metadata.remote$.data.cached_transformed["topic"].get();
            const peerset = report_builder.remote.peerset.data.get();

            const params: Parameters<IAPI["deltabase_data_api"]["content_extracts"]["get"]>["0"] = {
              company_id: instance.local.views.analysis.filters.applied_filters.company_id.get(),
              topic_name: instance.local.views.analysis.filters.applied_filters.topic_name.get(),
              year: instance.local.views.analysis.filters.applied_filters.timeframe.get(),
              sentiment_type: [],
            };

            if (params.company_id?.length === 0) {
              return {};
            }
            const response = await api.deltabase_data_api.content_extracts.get(params);

            const evidenceData: Record<string, Record<string, Record<string, Array<ITransformedContentExtract>>>> = {};

            response.data.forEach((extract) => {
              const { topic_name, company_id, text, sentiment_type } = extract;
              const topic = topic_metadata.find((t) => t.meta_data_value === topic_name);

              const group = topic?.meta_data_parent_value ?? topic_name;
              if (!group) return;

              // Initialize nested structure
              if (!evidenceData[group]) evidenceData[group] = {};
              if (!evidenceData[group][topic_name]) evidenceData[group][topic_name] = {};
              if (!evidenceData[group][topic_name][company_id]) evidenceData[group][topic_name][company_id] = [];

              const company = peerset[company_id];

              const cleanedExtract: ITransformedContentExtract = {
                ...extract,
                text: text.replace(/['"]/g, ""),
                company: company ?? null,
              };

              const currentExtracts = evidenceData[group][topic_name][company_id];
              const hasBothSentiments =
                (params?.sentiment_type?.includes("Positive") && params?.sentiment_type?.includes("Negative")) ||
                params?.sentiment_type?.length === 0;

              if (hasBothSentiments) {
                // If we want both sentiments, ensure we get one positive and one negative
                if (sentiment_type === "Positive" && !currentExtracts.some((e) => e.sentiment_type === "Positive")) {
                  currentExtracts.unshift(cleanedExtract); // Add positive first
                } else if (
                  sentiment_type === "Negative" &&
                  !currentExtracts.some((e) => e.sentiment_type === "Negative")
                ) {
                  currentExtracts.push(cleanedExtract); // Add negative second
                }
              } else if (currentExtracts.length < 2) {
                // If not filtering by both sentiments, just add up to 2 extracts
                currentExtracts.push(cleanedExtract);
              }
            });

            // Sort topics alphabetically within each group
            for (const group in evidenceData) {
              const sortedTopics = Object.keys(evidenceData[group]).sort();
              const sortedGroupData: Record<string, Record<string, Array<ITransformedContentExtract>>> = {};

              sortedTopics.forEach((topicName) => {
                sortedGroupData[topicName] = evidenceData[group][topicName];
              });

              evidenceData[group] = sortedGroupData;
            }

            legend.state.batch(() => {
              instance.remote.evidence.pending.set(false);
              instance.remote.evidence.error.set(false);
            });
            return evidenceData;
          } catch (error) {
            legend.state.batch(() => {
              instance.remote.evidence.error.set(true);
            });
          } finally {
            instance.remote.evidence.pending.set(false);
          }
        },
        waitFor: () => {
          const token = auth.state$.token.get();
          return !!token;
        },
      }),
    },
  };

  return { local, remote };
}

export const actions: IActions = {
  views: {
    analysis: {
      selectors: {
        measure: {
          set: (instance_id: string, measure_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const measure = instance.local.config.measure.measures.get().find((m) => m.id === measure_id);
            if (!measure) return;
            legend.state.batch(() => {
              instance.local.views.analysis.selectors.measure.selectedMeasureId.set(measure_id);
              instance.local.views.analysis.chart_config.polar_axis.assign({
                label: `${measure.label}  ${measure.unit ? `(${measure.unit})` : ""}`,
                domain: measure.domain,
                compactValues: measure.compactYAxisValues,
                unit: measure.unit,
              });
            });
          },
        },
        visualisation: {
          set: (instance_id: string, visualisation: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            instance.local.views.analysis.selectors.visualisation.selected.set(visualisation);
          },
        },
        peer_set_avg_type: {
          set: (instance_id: string, peer_set_avg_type: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            instance.local.views.analysis.selectors.peer_set_avg_type.selected.set(peer_set_avg_type);
          },
          apply: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const selected = instance.local.views.analysis.selectors.peer_set_avg_type.selected.get();
            instance.local.views.analysis.selectors.peer_set_avg_type.applied.set(selected);
          },
        },
        peer_set_avg_mode: {
          set: (instance_id: string, peer_set_avg_mode: boolean) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            instance.local.views.analysis.selectors.peer_set_avg_mode.selected.set(peer_set_avg_mode);
          },
          apply: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const selected = instance.local.views.analysis.selectors.peer_set_avg_type.selected.get();
            instance.local.views.analysis.selectors.peer_set_avg_type.applied.set(selected);
          },
        },
        geo: {
          set: (instance_id, geo) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.state.batch(() => {
              instance.local.views.analysis.selectors.geo.selectedLocationId.set(geo.location_id);
              instance.local.views.analysis.selectors.geo.selectedLocationType.set(geo.location_type);
            });
          },
        },
      },
      filters: {
        benchmark: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const filters = instance.local.views.analysis.filters.benchmark.data.get();
            const selected_measure = instance.local.views.analysis.selectors.measure.selectedMeasureId.get();

            const benchmarks: string[] = [];
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                const benchmark = instance.local.config.benchmark.benchmarks.get().find((b) => b.id === filter.id);
                if (benchmark?.id === "PEER_SET_AVG") {
                  benchmarks.push(benchmark.id);
                }
              }
            });
            instance.local.views.analysis.filters.applied_filters.benchmarks.set(benchmarks);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.benchmark,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.benchmark,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.benchmark,
            });
            return out;
          },
        },
        peer_set_avg_contributors: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.peer_set_avg_contributors.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.peer_set_avg_contributors.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.peer_set_avg_contributors,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.peer_set_avg_contributors,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.peer_set_avg_contributors,
            });
            return out;
          },
        },
        company: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.company.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.company_id.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.company,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.company,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.company,
            });
            return out;
          },
        },
        topic: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.topic.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.topic_name.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.topic,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.topic,
              params: { group: filter_group },
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.topic,
            });
            return out;
          },
        },
        timeframe: {
          applyAll: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const applied: string[] = [];
            const filters = instance.local.views.analysis.filters.timeframe.data.get();
            Object.values(filters).forEach((filter) => {
              if (filter.checked) {
                applied.push(filter.id);
              }
            });
            instance.local.views.analysis.filters.applied_filters.timeframe.set(applied);
          },
          toggle: (instance_id: string, filter_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggle({
              state: instance.local.views.analysis.filters.timeframe,
              params: { id: filter_id },
            });
          },
          toggleGroup: (instance_id: string, filter_group: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            legend.features.filter.toggleGroup({
              state: instance.local.views.analysis.filters.timeframe,
              params: { group: filter_group },
            });
          },
          toggleByRange: (instance_id, range) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const filters_obs = instance.local.views.analysis.filters.timeframe.data;
            const filters_arr = Object.values(filters_obs.get());
            const [startYear, endYear] = range.split("-");

            // For single year selection
            if (!endYear) {
              const targetFilter = filters_arr.find((filter) => filter.id === startYear);
              const newCheckedState = !targetFilter?.checked;

              filters_arr.forEach((filter) => {
                const checked = filter.id === startYear ? newCheckedState : false;
                filters_obs[filter.id].checked.set(checked);
              });
              return;
            }

            // For year range selection
            legend.state.batch(() => {
              // Check if all filters in range are already checked
              const allInRangeChecked = filters_arr.every((filter) => {
                const year = parseInt(filter.id);
                const inRange = year >= parseInt(startYear) && year <= parseInt(endYear);
                return !inRange || filter.checked;
              });

              // Toggle all filters in range based on current state
              // Get current checked state
              const currentCheckedFilters = filters_arr
                .filter((f) => f.checked)
                .map((f) => parseInt(f.id))
                .sort();

              // Get target range years
              const targetYears = Array.from(
                { length: parseInt(endYear) - parseInt(startYear) + 1 },
                (_, i) => parseInt(startYear) + i,
              ).sort();

              // Compare if current matches target range
              const isCurrentRange = JSON.stringify(currentCheckedFilters) === JSON.stringify(targetYears);

              filters_arr.forEach((filter) => {
                const year = parseInt(filter.id);
                const inRange = year >= parseInt(startYear) && year <= parseInt(endYear);
                // If current selection matches target range, toggle them all off
                // Otherwise set the new range
                const checked = isCurrentRange ? false : inRange;
                filters_obs[filter.id].checked.set(checked);
              });
            });
          },
          getFiltersByGroup: (instance_id: string) => {
            const instance = rc.registry.actions.getInstance(instance_id);
            const out = legend.features.filter.getFiltersByGroup({
              state: instance.local.views.analysis.filters.timeframe,
            });
            return out;
          },
        },
      },
    },
  },
};

export const observers: IObservers = {
  view: {
    analysis: {
      chart_config: {
        sync_to_state: (instance_id: string) => {
          if (!instance_id) return;
          legend.state.observe(() => {
            const instance = rc.registry.actions.getInstance(instance_id);

            const peerset = report_builder.remote.peerset.data.get();

            // sync chart config
            const applied_company_id = instance.local.views.analysis.filters.applied_filters.company_id.get() ?? [];
            const applied_benchmarks = instance.local.views.analysis.filters.applied_filters.benchmarks.get() ?? [];

            const config: z.infer<typeof local_schema>["views"]["analysis"]["chart_config"]["entities"] = {};

            // add benchmark entities to chart config
            applied_benchmarks.forEach((benchmark_id, idx) => {
              const benchmark = instance.local.config.benchmark.benchmarks.get().find((b) => b.id === benchmark_id);
              if (!benchmark) return;

              if (benchmark.id === "PEER_SET_AVG") {
                config[benchmark.id] = {
                  id: benchmark.id,
                  label: benchmark.label,
                  color: `hsl(var(--chart-deltabase-peer_set_avg))`,
                };
              }
            });

            // add company entities to chart config
            applied_company_id.forEach((company_id, idx) => {
              const company = peerset[company_id];
              if (!company) return;
              config[company_id] = {
                id: company_id,
                label: company?.company_name ?? "",
                color: `hsl(var(--chart-deltabase-company-${idx + 1}))`,
              };
            });

            instance.local.views.analysis.chart_config.entities.set(config);
          });
        },
      },
      filters: {
        benchmark: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ initialised: boolean }>((e) => {
              if (e.previous?.initialised) return;
              const instance = rc.registry.actions.getInstance(instance_id);
              const benchmarks = instance.local.config.benchmark.benchmarks.get();

              if (!benchmarks || benchmarks.length === 0) return;

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.benchmark,
                  params: {
                    data: benchmarks.map((benchmark) => {
                      const out = {
                        id: benchmark.id,
                        label: benchmark.label,
                        checked: benchmark.filter.checked,
                        disabled: benchmark.filter.disabled,
                        group: "",
                      };
                      return out;
                    }),
                  },
                });
                actions.views.analysis.filters.benchmark.applyAll(instance_id);
              });

              return { initialised: true };
            });
          },
        },
        peer_set_avg_contributors: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ peerset_key: string }>((e) => {
              const instance = rc.registry.actions.getInstance(instance_id);
              const peerset = report_builder.remote.peerset.data.get();
              const current_peerset_key = JSON.stringify(Object.keys(peerset));

              if (!peerset || current_peerset_key.length === 0 || current_peerset_key === e.previous?.peerset_key) {
                return;
              }

              const companies = Object.values(peerset);

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.peer_set_avg_contributors,
                  params: {
                    data: companies.map((company, idx) => ({
                      id: company.company_id,
                      label: company.company_name,
                      checked: true,
                      disabled: false,
                      group: "",
                    })),
                  },
                });
                actions.views.analysis.filters.peer_set_avg_contributors.applyAll(instance_id);
              });

              const peerset_key = JSON.stringify(Object.keys(peerset));

              return { peerset_key };
            });
          },
        },
        company: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ peerset_key: string }>((e) => {
              const instance = rc.registry.actions.getInstance(instance_id);
              const peerset = report_builder.remote.peerset.data.get();
              const current_peerset_key = JSON.stringify(Object.keys(peerset));

              if (!peerset || current_peerset_key.length === 0 || current_peerset_key === e.previous?.peerset_key) {
                return;
              }

              const companies = Object.values(peerset);

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.company,
                  params: {
                    data: companies.map((company, idx) => ({
                      id: company.company_id,
                      label: company.company_name,
                      checked: true,
                      disabled: false,
                      group: "",
                    })),
                  },
                });
                actions.views.analysis.filters.company.applyAll(instance_id);
              });

              const peerset_key = JSON.stringify(Object.keys(peerset));

              return { peerset_key };
            });
          },
        },
        topic: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ initialised: boolean }>((e) => {
              if (e.previous?.initialised) return;
              const instance = rc.registry.actions.getInstance(instance_id);
              const topic_metadata = metadata.remote$.data.get().cached_transformed["topic"];

              if (!topic_metadata || topic_metadata.length === 0) return;

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.topic,
                  params: {
                    data: topic_metadata.map((topic) => ({
                      id: topic.meta_data_value,
                      label: topic.meta_data_value,
                      checked: true,
                      disabled: false,
                      group: topic.meta_data_parent_value ?? "",
                    })),
                  },
                });
                actions.views.analysis.filters.topic.applyAll(instance_id);
              });

              return { initialised: true };
            });
          },
        },
        timeframe: {
          init: (instance_id: string) => {
            if (!instance_id) return;
            legend.state.observe<{ initialised: boolean }>((e) => {
              if (e.previous?.initialised) return;
              const instance = rc.registry.actions.getInstance(instance_id);
              const timeframe_metadata = metadata.remote$.data.get().cached_transformed["year"];
              const company_search_timeframe = report_builder.local.search.time_periods.get();

              if (!timeframe_metadata || timeframe_metadata.length === 0 || company_search_timeframe.length === 0) {
                return { initialised: false };
              }

              // populate filter state
              legend.state.batch(() => {
                legend.features.filter.batchAddFilter({
                  state: instance.local.views.analysis.filters.timeframe,
                  params: {
                    data: timeframe_metadata.map((timeframe) => {
                      const out = {
                        id: timeframe.meta_data_value,
                        label: timeframe.meta_data_value.toLowerCase(),
                        checked: company_search_timeframe?.includes(timeframe.meta_data_value) ?? false,
                        disabled: false,
                        group: "",
                      };
                      return out;
                    }),
                  },
                });
                actions.views.analysis.filters.timeframe.applyAll(instance_id);
              });

              return { initialised: true };
            });
          },
        },
      },
    },
  },
};

const helpers = {
  validateAndMergeState: (state: ILocalState$, new_state?: object) => {
    if (new_state !== undefined) {
      const isValid = local_schema.safeParse(new_state);
      if (!isValid.success) {
        throw new Error(
          `Invalid props provided to createState$. Validation errors: ${JSON.stringify(isValid.error.format(), null, 2)}`,
        );
      }
      state = merge(state, new_state);
      return state;
    }
    return state;
  },

  transformMeasureScore: (measure: IMeasure, measure_type: string) => {
    if (measure_type === "NSS") {
      const score = transforms.nss.toPercentage(measure.measure_value);
      return score;
    } else if (measure_type === "PREVALENCE") {
      const score = transforms.prevalence.toPercentage(measure.measure_value);
      return score;
    } else if (measure_type === "RATING") {
      const score = transforms.rating.toNumber(measure.measure_value);
      return score;
    } else if (measure_type === "REVIEW_COUNT") {
      const score = transforms.count.toNumber(measure.measure_value);
      return score;
    }

    return null;
  },
  mapMeasureNameToParams(
    measureMap: Record<string, { default: string; withGeoContinent: string; withGeoCountry: string }>,
    selected_measure_id: string,
    selected_location_id: string | undefined,
    selected_location_type: string | undefined,
  ) {
    if (selected_location_id && selected_location_type) {
      if (selected_location_type === "continent") {
        const measure_name = measureMap[selected_measure_id];
        return measure_name.withGeoContinent;
      } else if (selected_location_type === "country") {
        const measure_name = measureMap[selected_measure_id];
        return measure_name.withGeoCountry;
      }
    }
    const measure_name = measureMap[selected_measure_id];
    return measure_name.default;
  },
  formatChartData: (props: {
    data: IMeasure[];
    selected_measure_id: string;
    benchmark_config: z.infer<typeof local_schema>["config"]["benchmark"]["benchmarks"];
    company_config: z.infer<typeof local_schema>["config"]["company"];
    selected_location_id: string | undefined;
    selected_location_type: string | undefined;
  }) => {
    const {
      data,
      selected_measure_id,
      benchmark_config,
      company_config,
      selected_location_id,
      selected_location_type,
    } = props;

    // group by topic
    const grouped_data: Record<string, IMeasure[]> = {};
    data.forEach((measure) => {
      const topic = measure.topic_name;
      if (!grouped_data[topic]) {
        grouped_data[topic] = [];
      }
      grouped_data[topic].push(measure);
    });

    type ChartRecord = Record<string, number | string | null>;
    const chart_data: ChartRecord[] = [];

    Object.entries(grouped_data).forEach(([topic, measures]) => {
      const entry: ChartRecord = { topic: topic };
      measures.forEach((measure) => {
        const score =
          measure.measure_signal === "NO_SIGNAL" ? null : helpers.transformMeasureScore(measure, selected_measure_id);

        // Identify which entity the measure belongs to
        const matchToBenchmark = benchmark_config.find((benchmark) => {
          const benchmark_measure_name = helpers.mapMeasureNameToParams(
            benchmark.measure_filter_to_measure_name_map,
            selected_measure_id,
            selected_location_id,
            selected_location_type,
          );
          return benchmark_measure_name === measure.measure_name;
        });
        const matchToCompany =
          helpers.mapMeasureNameToParams(
            company_config.measure_filter_to_measure_name_map,
            selected_measure_id,
            selected_location_id,
            selected_location_type,
          ) === measure.measure_name;

        // If the measure belongs to a benchmark, add it to the chart data
        if (matchToBenchmark) {
          entry[matchToBenchmark.id] = score;
          return;
        }

        // If the measure belongs to the company, add it to the chart data
        if (matchToCompany) {
          entry[measure.company_id] = score;
          return;
        }

        // If the measure doesn't belong to a benchmark or the company, throw an error
        throw new Error(`Measure not associated with a benchmark or company entity: ${measure.measure_name}`);
      });
      chart_data.push(entry);
    });

    // Check if all values across all records are null
    const allValuesNull = chart_data.every((record) => {
      // Get all values except 'year' field
      const values = Object.entries(record)
        .filter(([key]) => key !== "topic")
        .map(([_, value]) => value);
      return values.every((value) => value === null);
    });

    if (allValuesNull) {
      return [];
    }

    return chart_data;
  },
  formatTableData: (props: {
    data: IMeasure[];
    selected_measure_id: string;
    benchmark_config: z.infer<typeof local_schema>["config"]["benchmark"]["benchmarks"];
    company_config: z.infer<typeof local_schema>["config"]["company"];
    peerset: Record<string, IPeersetCompany>;
    selected_location_id: string | undefined;
    selected_location_type: string | undefined;
  }) => {
    const {
      data,
      selected_measure_id,
      benchmark_config,
      company_config,
      peerset,
      selected_location_id,
      selected_location_type,
    } = props;

    // group by company
    const grouped_data: Record<
      string,
      {
        label: string;
        measures: IMeasure[];
      }
    > = {};
    data.forEach((measure) => {
      const matchToBenchmark = benchmark_config.find((benchmark) => {
        const benchmark_measure_name = helpers.mapMeasureNameToParams(
          benchmark.measure_filter_to_measure_name_map,
          selected_measure_id,
          selected_location_id,
          selected_location_type,
        );
        return benchmark_measure_name === measure.measure_name;
      });
      const matchToCompany =
        helpers.mapMeasureNameToParams(
          company_config.measure_filter_to_measure_name_map,
          selected_measure_id,
          selected_location_id,
          selected_location_type,
        ) === measure.measure_name;

      // If the measure belongs to a benchmark, add it to the chart data
      if (matchToBenchmark) {
        if (!grouped_data[matchToBenchmark.id]) {
          grouped_data[matchToBenchmark.id] = {
            label: matchToBenchmark.label,
            measures: [],
          };
        }
        grouped_data[matchToBenchmark.id].measures.push(measure);
        return;
      }

      // If the measure belongs to the company, add it to the chart data
      if (matchToCompany) {
        const company = peerset[measure.company_id];
        if (!grouped_data[measure.company_id]) {
          grouped_data[measure.company_id] = {
            label: company?.company_name,
            measures: [],
          };
        }
        grouped_data[measure.company_id].measures.push(measure);
        return;
      }
    });

    type TableRecord = Record<
      string,
      {
        id: string;
        score: number | string | null;
        signal?: string;
        label: string;
      }
    >;
    const benchmark_table_data: TableRecord[] = [];
    const company_table_data: TableRecord[] = [];

    Object.entries(grouped_data).forEach(([entity_id, { label, measures }]) => {
      const entry: TableRecord = {
        company_id: {
          id: entity_id,
          score: entity_id,
          label: label,
        },
      };
      measures.forEach((measure) => {
        const score =
          measure.measure_signal === "NO_SIGNAL" ? null : helpers.transformMeasureScore(measure, selected_measure_id);

        entry[measure.topic_name] = {
          id: measure.topic_name,
          score: score,
          label: measure.topic_name,
          signal: measure.measure_signal,
        };
      });

      // Check if this entry is for a benchmark
      const isBenchmark = benchmark_config.some((benchmark) => benchmark.id === entity_id);
      if (isBenchmark) {
        benchmark_table_data.push(entry);
      } else {
        company_table_data.push(entry);
      }
    });

    const table_data = [...benchmark_table_data, ...company_table_data];

    // Check if all values across all records are null
    const allValuesNull = table_data.every((record) => {
      // Get all values except 'year' field
      const values = Object.entries(record)
        .filter(([key]) => key !== "topic")
        .map(([_, value]) => value);
      return values.every((value) => value === null);
    });

    if (allValuesNull) {
      return [];
    }

    return table_data;
  },
};
